import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Modal,
  Card,
  CardContent,
  IconButton,
  Avatar,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

const drawerWidth = 240;

// Base URL used to resolve the "~/..." relative paths that come back from
// the API/session storage (e.g. "~/EmployeeImage/user.jpg").
// Point this at wherever those files are actually served from.
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_BASE_URL || '';

const resolveAssetUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const cleaned = path.replace(/^~\//, '').replace(/^\/+/, '');
  return `${ASSET_BASE_URL}/${cleaned}`;
};

// Parses the values written to sessionStorage. Everything is stored as a
// plain string, so numbers/booleans/dates need to be coerced.
const readProfileFromSession = () => {
  const get = (key) => sessionStorage.getItem(key);

  const clientName = get('clientName') || '';
  const userType = get('userType') || '';
  const clientCode = get('clientCode') || '';
  const employeeId = get('employeeId');
  const branchId = get('branchId');
  const classId = get('classId');
  const isMaster = get('is_Master') === 'true';
  const login = get('login') === 'true';
  const lastLoginOn = get('lastLoginOn') || '';
  const expirydateRaw = get('expirydate');
  const leftdayRaw = get('leftday');
  const clientImage = get('clientImage');
  const logo = get('logo');
  const clientLogo = get('clientLogo');

  const expirydate = expirydateRaw ? new Date(expirydateRaw) : null;
  const leftday = leftdayRaw !== null && leftdayRaw !== undefined && leftdayRaw !== ''
    ? Number(leftdayRaw)
    : null;

  return {
    clientName,
    userType,
    clientCode,
    employeeId,
    branchId,
    classId,
    isMaster,
    login,
    lastLoginOn,
    expirydate,
    leftday,
    // Prefer a personal photo, then a client logo, then a generic logo.
    avatarSrc: resolveAssetUrl(clientImage) || resolveAssetUrl(clientLogo) || resolveAssetUrl(logo),
  };
};

const formatDate = (date) => {
  if (!date || Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB'); // dd/mm/yyyy
};

const ProfileDetails = () => {
  const [openModal, setOpenModal] = useState(false);
  const [profile, setProfile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [renewalStatus, setRenewalStatus] = useState('idle'); // idle | submitting | submitted
  const navigate = useNavigate();

  useEffect(() => {
    setProfile(readProfileFromSession());
  }, []);

  const subscriptionStatus = useMemo(() => {
    if (!profile) return null;
    if (!profile.login) return { label: 'Inactive', color: 'error' };
    if (profile.leftday !== null && profile.leftday <= 0) return { label: 'Expired', color: 'error' };
    if (profile.leftday !== null && profile.leftday <= 7) return { label: 'Expiring Soon', color: 'warning' };
    return { label: 'Active', color: 'success' };
  }, [profile]);

  const handleModalOpen = () => setOpenModal(true);
  const handleModalClose = () => {
    setOpenModal(false);
    setRenewalStatus('idle');
  };

  const handleConfirmRenewal = () => {
    setRenewalStatus('submitting');
    // TODO: replace with the real renewal-request API call.
    setTimeout(() => {
      setRenewalStatus('submitted');
    }, 800);
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
    // NOTE: this only previews locally. To persist it, upload `file` to
    // the server here and update `clientImage` in sessionStorage/state
    // once the upload succeeds.
  };

  if (!profile) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const initials = profile.clientName ? profile.clientName.trim().charAt(0).toUpperCase() : '?';

  const detailRows = [
    { label: 'Name', value: profile.clientName || 'N/A' },
    { label: 'Account', value: profile.clientCode || 'N/A' },
    { label: 'Role', value: profile.userType || 'Staff' },
    { label: 'Last Login', value: profile.lastLoginOn || 'N/A' },
    { label: 'Expiry Date', value: formatDate(profile.expirydate) },
    {
      label: 'Days Left',
      value: profile.leftday !== null ? profile.leftday : 'N/A',
      valueColor: profile.leftday !== null && profile.leftday <= 7 ? 'warning.main' : undefined,
    },
  ];

  return (
    <Box
      component="main"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        mt: { xs: 0, sm: '64px' },
        p: { xs: 2, md: 4 },
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
      }}
    >
      <Container maxWidth="md">
        {/* Back Button - Fixed Top Left */}
        <Box sx={{ position: 'fixed', top: { xs: 16, sm: 80 }, left: { xs: 16, md: `calc(${drawerWidth}px + 16px)` }, zIndex: 1100 }}>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label="Go back"
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 3,
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>

        {/* Profile Card */}
        <Card
          sx={{
            mt: { xs: 2, sm: 4 },
            borderRadius: 4,
            boxShadow: 6,
            overflow: 'visible',
            background: 'linear-gradient(135deg, #fff7ed 0%, #fff0f5 100%)',
          }}
        >
          <CardContent sx={{ pt: 6, pb: 5, px: { xs: 3, md: 6 } }}>
            <Typography
              variant="h4"
              sx={{
                mb: 5,
                fontWeight: 'bold',
                color: '#1976d2',
                textAlign: 'center',
              }}
            >
              Profile Details
            </Typography>

            <Grid container spacing={4} alignItems="center">
              {/* Left Side: Profile Details */}
              <Grid item xs={12} md={6}>
                <Stack spacing={2.5}>
                  {detailRows.map((row) => (
                    <Typography key={row.label} variant="h6" sx={{ fontWeight: 600 }}>
                      {row.label}:{' '}
                      <span style={{ fontWeight: 400 }}>
                        {row.label === 'Days Left' && row.valueColor ? (
                          <Box component="span" sx={{ color: row.valueColor, fontWeight: 400 }}>
                            {row.value}
                          </Box>
                        ) : (
                          row.value
                        )}
                      </span>
                    </Typography>
                  ))}

                  <Box>
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600, mr: 1 }}>
                      Subscription Status:
                    </Typography>
                    {subscriptionStatus && (
                      <Chip
                        label={subscriptionStatus.label}
                        color={subscriptionStatus.color}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Box>
                </Stack>
              </Grid>

              {/* Right Side: Profile Image Upload */}
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    src={avatarPreview || profile.avatarSrc || undefined}
                    sx={{
                      width: 180,
                      height: 180,
                      mx: 'auto',
                      mb: 3,
                      border: '4px solid #fff',
                      boxShadow: 4,
                      bgcolor: '#e3f2fd',
                      fontSize: '4rem',
                    }}
                  >
                    {!avatarPreview && !profile.avatarSrc ? initials : null}
                  </Avatar>

                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<PhotoCameraIcon />}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontSize: '1rem',
                    }}
                  >
                    Upload Photo
                    <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Request Renewal Button */}
            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Button
              disabled
                variant="contained"
                color="primary"
                size="large"
                onClick={handleModalOpen}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 3,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}
              >
                Request Renewal
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Renewal Confirmation Modal */}
      <Modal open={openModal} onClose={handleModalClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 400 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
            Request Renewal
          </Typography>

          {renewalStatus === 'submitted' ? (
            <>
              <Typography variant="body1" sx={{ mb: 4 }}>
                Your renewal request has been submitted.
              </Typography>
              <Button variant="contained" color="primary" onClick={handleModalClose} sx={{ minWidth: 120 }}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 4 }}>
                Are you sure you want to request a renewal of your subscription?
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirmRenewal}
                  disabled={renewalStatus === 'submitting'}
                  sx={{ minWidth: 120 }}
                >
                  {renewalStatus === 'submitting' ? <CircularProgress size={22} color="inherit" /> : 'Confirm'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleModalClose}
                  disabled={renewalStatus === 'submitting'}
                  sx={{ minWidth: 120 }}
                >
                  Cancel
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default ProfileDetails;